<template>
  <q-card
    v-ripple="!disable"
    class="col-auto column full-height noselect"
    :class="{
      'cursor-pointer': !disable,
      'bg-primary-light': !disable,
      'bg-grey-2': disable,
    }"
    @click="onSwitch"
  >
    <q-card-section
      class="col row items-center justify-center"
      :class="{
        'q-py-xs': tiny,
        'q-py-sm': dense && !tiny,
        'q-pb-xs': !dense && !tiny,
      }"
    >
      <q-badge
        color="positive"
        floating
      >
        {{ modelValue }}
      </q-badge>
      <span v-if="!dense">
        {{ $t('programs.dataCollection.intervalTimer.textTitle') }}
      </span>
      <span
        v-else
        :class="{ 'text-subtitle2': !tiny }"
      >{{ currentTimeFormatted }}</span>
      <q-icon
        v-if="!disable"
        :name="timerActionIcon"
        color="primary"
        size="xs"
        class="q-pl-xs"
      />
    </q-card-section>
    <q-card-section
      v-if="!dense"
      :class="{
        'bg-primary': !disable,
        'bg-dark-variant': disable,
      }"
      class="text-center full-width text-subtitle2 q-py-xs items-center  text-white"
    >
      <span>{{ currentTimeFormatted }}</span>
    </q-card-section>
  </q-card>
</template>
<script>
import { intervalWarningSounds } from 'boot/globals';
import { chain, isDate, isNil } from 'lodash';
import Toolbox from 'src/mixins/Toolbox';

export default {
  name: 'IntervalTimer',

  mixins: [Toolbox],

  props: {
    modelValue: {
      type: Number,
      default: 0,
    },
    duration: {
      type: Number,
      default: 0,
    },
    intervalTime: {
      type: Number,
      required: true,
    },
    timerIntervalPause: {
      type: Boolean,
      default: false,
    },
    paused: {
      type: Boolean,
      default: false,
    },
    durationOffset: {
      type: Date,
      default: null,
    },

    disable: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    tiny: {
      type: Boolean,
      default: false,
    },

    warning: {
      type: Number,
      default: null,
    },
    warningSound: {
      type: String,
      default: '',
    },
    iosAudio: {
      type: Audio,
      default: null,
    },
    muteAudio: {
      type: Boolean,
      default: false,
    },

    sessionDurationOffset: {
      type: Date,
      default: null,
    },
    sessionPaused: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update', 'start', 'pause', 'update:modelValue', 'record'],

  data() {
    return {
      timerStart: null,
      warningPlayed: false,

      currentTime: 0,
      worker: new Worker('/timer.web-worker.js'),
      timerInterval: null,

      audio: null,
      showAudioDialog: false,
      isUpdatingDuration: false,
      // The `duration` prop only catches up after our own write round-trips through Firestore, and
      // the worker ticks every 50ms. Elapsed time is read from here so a rollover can settle the
      // accumulator immediately instead of counting it again on every tick until the write lands.
      pendingDuration: this.duration,
    };
  },

  computed: {

    isTimerStarted() {
      return (this.timerInterval !== null);
    }, // isTimerStarted()

    timerActionIcon() {
      return this.isTimerStarted ? 'pause' : 'play_arrow';
    }, // timerActionIcon()

    currentTimeFormatted() {
      /** This calculation is required when this.currentTime is negative and we don't want to display broken
       *  negative values to the user. It happens when refreshing the page or reopening session page with running
       *  interval program
       */
      const estimatedIntervalTime = this.intervalTime + (this.currentTime % this.intervalTime);
      const timeToFormat = this.currentTime < 0 ? estimatedIntervalTime : this.currentTime;
      return this.formatDuration(timeToFormat, false);
    }, // currentTimeFormatted()

    isIosBrowser() {
      return this.$q.platform.is.ios && !this.$q.platform.is.capacitor;
    }, // isIosBrowser()

  },

  watch: {

    duration: {
      immediate: false,
      handler(duration) {
        this.pendingDuration = duration;
        this.isUpdatingDuration = false;
      },
    }, // duration()

    durationOffset: {
      immediate: false,
      handler(durationOffset) {
        // A stopped timer must not adopt an offset: interval targets share one data document, so a
        // sibling's pause write re-delivers this target's pre-pause offset, and getTime() would then
        // count the whole pause. startTimer() reads the prop itself. HR-4624
        if (isDate(durationOffset) && this.isTimerStarted) {
          this.timerStart = durationOffset;
        }
      },
    }, // durationOffset()

    paused: {
      immediate: true,
      handler(pausedState) {
        this.handlePausedState(pausedState);
      },
    }, // paused()

    sessionPaused: {
      immediate: false,
      handler(sessionPausedState) {
        this.handleSessionPausedState(sessionPausedState);
      },
    },

    iosAudio: {
      immediate: true,
      handler(audio) {
        if (!isNil(audio)) {
          this.audio = audio;
        }
      },
    },

  }, // watch()

  created() {
    this.currentTime = this.getCurrentTimeRounded();
    this.worker.addEventListener('message', this.handleWorkerMessage);
    this.setAudioInstance();
  },

  beforeUnmount() {
    this.stopTimer();
    if (this.worker) {
      this.worker.removeEventListener('message', this.handleWorkerMessage);
      this.worker.terminate();
    }
  },

  methods: {
    handleSessionPausedState(sessionPaused) {
      if (sessionPaused) {
        this.$emit('update', this.getTime());
        this.stopTimer();
        return;
      }

      if (!this.paused) {
        const timerOffset = this.sessionDurationOffset || new Date();
        this.$emit('update', this.getTime(), timerOffset);
        this.startTimer(timerOffset);
      }
    },

    handlePausedState(paused) {
      if (paused) {
        this.stopTimer();
        return;
      }

      if (!this.isTimerStarted && !this.sessionPaused) {
        const timerOffset = this.durationOffset || new Date();
        this.startTimer(timerOffset);
      }
    },
    /**
     * Public: Get current count-down time from timer in seconds
     */
    getTime() {
      if (!isDate(this.timerStart)) {
        return this.pendingDuration;
      }
      const curTime = new Date();
      return ((curTime.getTime() - this.timerStart.getTime()) / 1000) + this.pendingDuration;
    }, // getTime()

    /**
     * Previously we had a problem while returning back to session with already stopped program (this program should
     * have some time before new interval to warn set in setting (lets say 5 seconds)). In that scenario we could see 4
     * seconds in timer if we click resume session from home screen. Rounding of currentTime fixes this issue
     * because this.interval - this.getTime() could return a value like 4.98
     * */
    getCurrentTimeRounded() {
      return Math.round(this.intervalTime - this.getTime());
    }, // getCurrentTimeRounded

    /**
     * Called by parent to signal session finish
     */
    finishSession() {
      this.stopTimer();
    }, // finishSession()

    handleWorkerMessage(event) {
      // Check to ensure that duration is updated properly before we fire new onInterval after we pass 00:00 point
      if (event.data.type === 'tick' && !this.isUpdatingDuration) {
        this.onInterval();
      }
    },

    onSwitch() {
      if (this.disable) {
        return;
      }
      if (!this.isTimerStarted) {
        const timeFromTimerStart = this.getTime();
        const isTimeFromStartInvalid = timeFromTimerStart >= this.intervalTime;
        /**
         * After coming back from device snoozing mode program is stopped but we should make sure that clicking resume
         * button emits action with correct duration argument. We cannot use this.getTime() value because it will be
         * bigger than programs intervalTime. So we need to compute new value according to program settings.
         * */
        const validTimeFromTimerStart = isTimeFromStartInvalid && this.timerIntervalPause
          ? this.intervalTime - this.warning
          : timeFromTimerStart;
        this.$emit('start', validTimeFromTimerStart);
        return;
      }
      // Pause timer
      this.$emit('pause', this.getTime());
    }, // onSwitch()

    startTimer(timeOffset = null) {
      if (this.isTimerStarted) {
        // Prevent multiple simultaneous timers
        return;
      }
      this.worker.postMessage({
        type: 'startTimer',
      });
      this.timerStart = timeOffset || new Date();
      this.timerInterval = true;
    }, // startTimer()

    stopTimer() {
      if (!this.isTimerStarted) {
        return;
      }
      this.worker.postMessage({
        type: 'stopTimer',
      });
      this.timerInterval = null;
      this.timerStart = null;
    }, // stopTimer()

    onInterval() {
      if (!this.timerStart) {
        return;
      }

      const currentTime = this.getCurrentTimeRounded();
      const isIntervalWithPauseFiredAfterSnoozeMode = this.timerIntervalPause && currentTime < 0;

      if (currentTime <= this.warning && !this.warningPlayed) {
        if (currentTime > 0) {
          // Make sure this will play on safari - require re initialization of audio instance
          this.setAudioInstance();

          this.playWarning();
          this.warningPlayed = true;
        }

        if (this.timerIntervalPause) {
          this.$emit('pause', this.getTime());
        }
      }

      // using values with decimals here to ensure that we are not jumping from 00:00 to this.intervalTime in one tick
      if (this.intervalTime - this.getTime() <= 0) {
        if (isIntervalWithPauseFiredAfterSnoozeMode) {
          this.processWakeUpFromSnoozeMode();
        } else {
          const intervalsSkipped = Math.floor(-currentTime / this.intervalTime);
          this.warningPlayed = false;
          const currentTimestamp = new Date().getTime();
          // The whole elapsed time, accumulator included: a tab reopened after a group pause carries
          // an accumulator many intervals long, and leaving it out of the new offset would make every
          // following tick roll the interval over again until the write lands. HR-4624
          const elapsedMs = this.getTime() * 1000;
          const intervalMs = this.intervalTime * 1000;
          const lastTimerElapsedMs = elapsedMs > intervalMs ? elapsedMs % intervalMs : 0;
          this.timerStart = new Date(currentTimestamp - lastTimerElapsedMs);
          this.pendingDuration = 0;
          if (this.timerIntervalPause) {
            this.isUpdatingDuration = true;
          }
          this.$emit('update:modelValue', this.modelValue + intervalsSkipped + 1, false, this.timerStart);
        }
      }
      this.currentTime = isIntervalWithPauseFiredAfterSnoozeMode ? this.warning : currentTime;
    }, // onInterval()

    /**
     * This function helps to set up correct program data after device is coming back from snoozing mode.
     * In this scenario we should stop the program, do NOT increment last interval (because we did not pass 00:00) and
     * do NOT set up new timerStart time because it will happen later when we click resume button
     * */
    processWakeUpFromSnoozeMode() {
      this.warningPlayed = true;
      this.isUpdatingDuration = true;
      this.$emit('update:modelValue', this.modelValue, true, this.timerStart);
    }, // processWakeUpFromSnoozeMode()

    recordData() {
      this.$emit('record');
    }, // recordData()

    /**
     * Moved this initialization to a separate method to run it in created() lifecycle method to ensure that Safari
     * loads new Audio() in inactive browser tab
     * */
    setAudioInstance() {
      const soundFile = chain(intervalWarningSounds)
        .find({ value: this.warningSound })
        .get('soundFile')
        .value();

      if (!soundFile) {
        return;
      }

      if (this.isIosBrowser && this.audio) {
        this.audio.src = `audio/${soundFile}`;
        return;
      }

      if (!this.audio) {
        this.audio = new Audio(`audio/${soundFile}`);
      }
    }, // setAudioInstance()

    playWarning() {
      if (this.audio && !this.muteAudio) {
        this.audio.play();
      }
    }, // playWarning()

  },

};
</script>
